import { useState } from 'react';
import { Banknote, Building2, ChevronDown, Copy, Check, Mail, Printer } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  CHECK_PAYABLE_TO,
  MAIL_TO_LINES,
  PAY_TO_ACCOUNT,
  PAY_TO_ACCOUNT_NAME,
  PAY_TO_BANK,
  PAY_TO_ROUTING,
  REMIT_TO_EMAIL,
} from '@/lib/payToUs';

/**
 * "Pay us directly" — for the customer who does not want us pulling from
 * their account at all. Plenty of businesses pay every vendor out of their
 * bank's bill-pay screen and will never save a card here; before this panel
 * existed /pay told them to set up a payment method or nothing, which reads
 * as "we only take payment one way".
 *
 * These are the SAME bank details printed on every invoice and statement PDF
 * (see src/lib/payToUs.ts and its server twin), so a customer holding a paper
 * invoice and a customer on this screen are never given two different account
 * numbers.
 *
 * ⚠ The remittance line is not decoration. A bank transfer arrives as a bare
 * amount with no invoice numbers on it, so a lump payment with no advice has
 * to be chased by a human before it can be applied — which is the whole
 * reason remit@ is stated on every push route we offer.
 */
export function SelfPayPanel({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mt-6 rounded-2xl border border-brix-border bg-brix-surface/85 backdrop-blur-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-brix-primary-tint/20 transition-colors"
      >
        <Building2 size={19} className="mt-0.5 shrink-0 text-brix-primary" />
        <span className="min-w-0 flex-1">
          {/* Sky's wording, 2026-09-03. Reads as an offer, not a consolation
              prize — most of these customers pay every vendor out of their
              bank's bill-pay screen and there is nothing second-best about
              it. The remittance ask rides in the headline because a bank
              transfer arrives as a bare amount with no invoice numbers, and
              that is the one thing we need them to do. */}
          <span className="block text-base font-bold text-brix-ink">
            Want to pay us using check or ACH from your bank via bill pay?
          </span>
          <span className="block text-[15px] leading-relaxed text-brix-muted mt-0.5">
            No problem — here's the bank info. Just make sure you send remittances.
          </span>
        </span>
        <ChevronDown
          size={18}
          className={cn('mt-1 shrink-0 text-brix-muted transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="border-t border-brix-border px-5 py-5 space-y-5">
          <section>
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-tight text-brix-muted">
              <Banknote size={15} className="text-brix-primary" /> ACH / bank transfer
            </h3>
            <p className="mt-1.5 text-[15px] leading-relaxed text-slate-700">
              Set us up as a payee in your bank's bill-pay, or send an ACH to:
            </p>
            <dl className="mt-2.5 divide-y divide-brix-border rounded-xl border border-brix-border bg-brix-surface-alt/50">
              <BankRow label="Bank" value={PAY_TO_BANK} />
              <BankRow label="Account name" value={PAY_TO_ACCOUNT_NAME} />
              <BankRow label="Routing number" value={PAY_TO_ROUTING} mono copyable />
              <BankRow label="Account number" value={PAY_TO_ACCOUNT} mono copyable />
            </dl>
            <p className="mt-2.5 text-sm leading-relaxed text-brix-muted">
              That is everything you need — nothing to set up on your side. If your AP department
              wants these details on a{' '}
              <a href="#ach-setup" className="font-semibold text-brix-primary hover:underline">
                signed document
              </a>{' '}
              before it can add us as a vendor, there is one below.
            </p>
          </section>

          <section className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
            <h3 className="flex items-center gap-2 text-base font-bold text-amber-900">
              <Mail size={16} className="shrink-0" /> Please send us a remittance
            </h3>
            <p className="mt-1.5 text-[15px] leading-relaxed text-amber-900">
              If you send us an ACH, please email your remittance to{' '}
              <a href={`mailto:${REMIT_TO_EMAIL}`} className="font-bold underline">
                {REMIT_TO_EMAIL}
              </a>{' '}
              so we can apply it to the right invoices. A bank transfer reaches us as just an
              amount — the remittance is what tells us which invoices it pays.
            </p>
          </section>

          <section>
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-tight text-brix-muted">
              <Printer size={15} className="text-brix-primary" /> Mail a check
            </h3>
            <p className="mt-1.5 text-[15px] leading-relaxed text-slate-700">
              Make checks payable to <span className="font-bold">{CHECK_PAYABLE_TO}</span> and mail
              to:
            </p>
            <address className="mt-2 not-italic rounded-xl border border-brix-border bg-brix-surface-alt/50 px-4 py-3 text-[15px] leading-relaxed text-slate-900">
              {MAIL_TO_LINES.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </address>
            <p className="mt-2 text-sm text-brix-muted">
              Write your invoice numbers on the check or its stub so we can apply it correctly.
            </p>
          </section>

          <p className="text-sm text-brix-muted">
            Paying this way keeps working exactly as it does today — nothing here saves a card or
            gives us permission to charge you. Invoices show as paid once the money lands and we
            apply it, which is a few days behind a payment made on this page.
          </p>
        </div>
      )}
    </div>
  );
}

function BankRow({
  label,
  value,
  mono,
  copyable,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard is blocked in plenty of contexts (insecure origin, denied
      // permission). The number is on screen either way, so a failed copy is
      // not worth an error message.
    }
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <dt className="text-sm text-brix-muted w-32 shrink-0">{label}</dt>
      <dd
        className={cn('text-[15px] font-bold text-slate-900 min-w-0 break-all', mono && 'font-mono')}
        style={mono ? { fontVariantNumeric: 'tabular-nums' } : undefined}
      >
        {value}
      </dd>
      {copyable && (
        <button
          type="button"
          onClick={copy}
          className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-lg border border-brix-border px-2 py-1 text-xs font-bold text-brix-primary hover:bg-brix-primary-tint/30"
          aria-label={`Copy ${label.toLowerCase()}`}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}
